import React, { useMemo } from 'react';
import { useTranslate } from 'hooks/use-translate';
import { DialogValidity } from 'components/dialog-validity';
import { apiUserPatientProfile } from 'services/user-patient-profile';
import { calcBmi } from '../../../../utils/app-helpers';

interface Props {
  userPatientProfileID: string;
  onClose: () => void;
  onDone: () => void;
}

export const DialogValidityPatientFields: React.FC<Props> = ({
  userPatientProfileID,
  onClose,
  onDone,
}) => {
  const { tp } = useTranslate();
  const { data, isFetching, isSuccess } =
    apiUserPatientProfile.useGetPatientRequiredDetailsForGmel29Query(userPatientProfileID, {
      refetchOnMountOrArgChange: true,
    });

  const list = useMemo(() => {
    const weight = data?.weight;
    const height = data?.height;
    const isValidWeight = Number(weight) > 0;
    const isValidHeight = Number(height) > 0;
    const bmi = calcBmi(weight || 0, height || 0);
    const isValidBmi = isValidWeight && isValidHeight;
    const gender = data?.gender;
    const medicalRemarks = data?.medicalRemarks?.trim() ?? '';
    const requiresMedicalRemarks = isValidBmi && bmi < 30;

    return [
      { title: tp('weight'), value: weight, isValid: isValidWeight },
      { title: tp('height'), value: height, isValid: isValidHeight },
      { title: tp('bmi'), value: bmi, isValid: isValidBmi },
      ...(requiresMedicalRemarks
        ? [
            {
              title: tp('medical-remarks'),
              value: medicalRemarks,
              isValid: Boolean(medicalRemarks),
              info: tp('bmi-less-30'),
            },
          ]
        : []),
      {
        title: tp('gender'),
        value: gender?.title,
        isValid: gender?.isMissing === false,
      },
    ];
  }, [data, tp]);

  return (
    <DialogValidity
      title={tp('patient-gimel29-verification')}
      list={list}
      success={{
        title: tp('patient-gimel29-verified-success-title'),
        description: tp('patient-gimel29-verified-success-description'),
      }}
      error={{
        title: tp('patient-gimel29-verified-error-title'),
        description: tp('patient-gimel29-verified-error-description'),
      }}
      isLoading={isFetching}
      isLoadedSuccess={isSuccess}
      onSubmit={onDone}
      onClose={onClose}
    />
  );
};
